import React, { useState, useEffect, useCallback } from 'react';
import { adminGetEReporting, adminUpdateEReporting, adminGetTenants } from '../../api';
import api from '../../api';
import SearchSelect from '../SearchSelect';
import StatusBadge from '../StatusBadge';

const downloadAdminPDF = async (factureId, numero) => {
  try {
    const res = await api.get(`/admin/e-reporting/${factureId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `facture-${numero || factureId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    alert('Erreur lors du téléchargement du PDF');
  }
};

const STATUTS_EREPORTING = [
  { value: 'non_transmise', label: 'Non transmise' },
  { value: 'pret_a_transmettre', label: 'Prêt à transmettre' },
  { value: 'DEPOSEE', label: 'Déposée' },
  { value: 'EN_COURS', label: 'En traitement' },
  { value: 'ACCEPTEE', label: 'Acceptée' },
  { value: 'REFUSEE', label: 'Refusée' },
  { value: 'REJETEE', label: 'Rejetée' },
];

const FILTRE_OPTIONS = [
  { value: 'pret_a_transmettre', label: 'À transmettre (signalées par les clients)' },
  { value: 'all', label: 'Tous les statuts' },
  { value: 'non_transmise', label: 'Non transmises' },
  { value: 'DEPOSEE', label: 'Déposées' },
  { value: 'EN_COURS', label: 'En traitement' },
  { value: 'ACCEPTEE', label: 'Acceptées' },
  { value: 'REFUSEE', label: 'Refusées' },
  { value: 'REJETEE', label: 'Rejetées' },
];

function AdminEReporting() {
  const [factures, setFactures] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('pret_a_transmettre');
  const [editState, setEditState] = useState({});
  const [alert, setAlert] = useState(null);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    adminGetTenants().then(res => setTenants(res.data)).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    try {
      const params = { statut: statutFilter };
      if (tenantFilter) params.tenant_id = tenantFilter;
      const res = await adminGetEReporting(params);
      setFactures(res.data);
      setEditState({});
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur de chargement', 'error');
    }
  }, [statutFilter, tenantFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFieldChange = (factureId, field, value) => {
    setEditState(prev => ({
      ...prev,
      [factureId]: { ...prev[factureId], [field]: value },
    }));
  };

  const handleSave = async (f) => {
    const patch = editState[f.id] || {};
    const payload = {
      super_pdp_statut: patch.super_pdp_statut ?? f.super_pdp_statut,
    };
    if ('super_pdp_id' in patch) payload.super_pdp_id = patch.super_pdp_id;

    setEditState(prev => ({ ...prev, [f.id]: { ...prev[f.id], saving: true } }));
    try {
      await adminUpdateEReporting(f.id, payload);
      showAlert('Statut mis à jour');
      loadData();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la mise à jour', 'error');
      setEditState(prev => ({ ...prev, [f.id]: { ...prev[f.id], saving: false } }));
    }
  };

  const isDirty = (f) => {
    const patch = editState[f.id] || {};
    if ('super_pdp_statut' in patch && patch.super_pdp_statut !== f.super_pdp_statut) return true;
    if ('super_pdp_id' in patch && patch.super_pdp_id !== (f.super_pdp_id || '')) return true;
    return false;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>E-reporting</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ minWidth: 240 }}>
            <SearchSelect
              placeholder="Filtrer par entreprise..."
              options={tenants.map(t => ({ value: t.id, label: t.nom }))}
              value={tenantFilter}
              onChange={(val) => setTenantFilter(val)}
            />
          </div>
          <select
            className="form-control"
            style={{ minWidth: 220 }}
            value={statutFilter}
            onChange={e => setStatutFilter(e.target.value)}
          >
            {FILTRE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      {statutFilter === 'pret_a_transmettre' && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-primary)' }}>
            {factures.length}
          </span>
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
            facture{factures.length !== 1 ? 's' : ''} signalée{factures.length !== 1 ? 's' : ''} prête{factures.length !== 1 ? 's' : ''} à transmettre
          </span>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Entreprise</th>
              <th>Facture</th>
              <th>Client</th>
              <th>Montant TTC</th>
              <th>Date émission</th>
              <th>Statut e-reporting</th>
              <th>PDF</th>
              <th>Mettre à jour</th>
            </tr>
          </thead>
          <tbody>
            {factures.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  {statutFilter === 'pret_a_transmettre'
                    ? 'Aucune facture signalée prête à transmettre'
                    : 'Aucune facture pour ce filtre'}
                </td>
              </tr>
            ) : factures.map(f => {
              const patch = editState[f.id] || {};
              const currentStatut = patch.super_pdp_statut ?? f.super_pdp_statut;
              const currentId = patch.super_pdp_id !== undefined ? patch.super_pdp_id : (f.super_pdp_id || '');
              const saving = patch.saving || false;

              return (
                <tr key={f.id}>
                  <td style={{ fontWeight: 500 }}>{f.tenant_nom}</td>
                  <td className="num">{f.numero}</td>
                  <td>{f.client_nom || '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {Number(f.total_ttc).toFixed(2)} €
                  </td>
                  <td>{new Date(f.date_creation + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                  <td><StatusBadge statut={f.super_pdp_statut || 'non_transmise'} /></td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => downloadAdminPDF(f.id, f.numero)}
                      title="Télécharger le PDF Factur-X"
                    >
                      ↓ PDF
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        className="form-control"
                        style={{ minWidth: 150 }}
                        value={currentStatut}
                        onChange={e => handleFieldChange(f.id, 'super_pdp_statut', e.target.value)}
                        disabled={saving}
                      >
                        {STATUTS_EREPORTING.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="form-control"
                        style={{ minWidth: 140 }}
                        placeholder="ID Super PDP"
                        value={currentId}
                        onChange={e => handleFieldChange(f.id, 'super_pdp_id', e.target.value)}
                        disabled={saving}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleSave(f)}
                        disabled={saving || !isDirty(f)}
                      >
                        {saving ? '...' : 'Enregistrer'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminEReporting;
